import React from 'react';
import { Field } from 'formik';
import * as yup from 'yup';

import ProfilePictureWithDropdown from 'features/users/components/ProfilePictureWithDropdown/ProfilePictureWithDropdown';
import { Box } from 'shared/components/display';
import SocialMediaHandleInput from 'shared/components/SocialMediaHandleInput/SocialMediaHandleInput';
import TextInput from 'shared/components/TextInput';

import { YUP_FIELDS } from 'shared/utilities/yupFieldUtility';

interface Props {
  userId: number;
}

export const PROFILE_PICTURE_REQUIRED_MESSAGE = 'Please add a profile picture so brands can get to know you.';

export const PortfolioOnboardingStepFields: React.FC<Props> = ({ userId }) => {
  return (
    <Box className="portfolio-step">
      <Field
        name={PORTFOLIO_FIELDNAMES.PROFILE_PICTURE}
        component={ProfilePictureWithDropdown}
        userId={userId}
        dataCy="profile-picture"
      />
      <Field
        name={PORTFOLIO_FIELDNAMES.PORTFOLIO_LINK}
        component={TextInput}
        label="Portfolio Link"
        placeholder="Portfolio Link"
      />
      <Field
        name={PORTFOLIO_FIELDNAMES.INSTAGRAM_HANDLE}
        label="Instagram Handle"
        placeholder="Instagram Handle"
        component={SocialMediaHandleInput}
        dataCy="instagram-handle"
      />
      <Field
        name={PORTFOLIO_FIELDNAMES.TIKTOK_HANDLE}
        label="TikTok Handle"
        placeholder="TikTok Handle"
        component={SocialMediaHandleInput}
        dataCy="tiktok-handle"
      />
    </Box>
  );
};

export const PORTFOLIO_FIELDNAMES = {
  PORTFOLIO_LINK: 'portfolioLink',
  TIKTOK_HANDLE: 'tiktokHandle',
  INSTAGRAM_HANDLE: 'instagramHandle',
  PROFILE_PICTURE: 'profilePic',
};

/**
 * The picture is satisfied by a freshly chosen `file` or by an already-uploaded `url`.
 * A blob: url from a previous session does not survive localStorage, so a reloaded draft
 * correctly asks for the picture again.
 */
export const hasProfilePictureValue = (value?: { url?: string; file?: File }): boolean =>
  Boolean(value?.file || value?.url);

/**
 * The picture is only required during onboarding. PortfolioForm in creator account settings
 * shares this schema and has no picture field, so requiring it unconditionally would leave
 * that form permanently invalid.
 */
export const getPortfolioSchema = ({ requireProfilePicture = false }: { requireProfilePicture?: boolean } = {}) =>
  yup.object({
    ...(requireProfilePicture
      ? {
          [PORTFOLIO_FIELDNAMES.PROFILE_PICTURE]: yup
            .object()
            .test('profile-picture-required', PROFILE_PICTURE_REQUIRED_MESSAGE, hasProfilePictureValue),
        }
      : {}),
    [PORTFOLIO_FIELDNAMES.PORTFOLIO_LINK]: YUP_FIELDS.PORTFOLIO_LINK_OPTIONAL,
    [PORTFOLIO_FIELDNAMES.TIKTOK_HANDLE]: yup.string().nullable().tiktokHandle(),
    [PORTFOLIO_FIELDNAMES.INSTAGRAM_HANDLE]: yup.string().nullable().instagramHandle(),
  });

export const getPortfolioInitialValues = (profilePic?: { url?: string } | null) => ({
  [PORTFOLIO_FIELDNAMES.PROFILE_PICTURE]: profilePic?.url ? profilePic : {},
  [PORTFOLIO_FIELDNAMES.PORTFOLIO_LINK]: '',
  [PORTFOLIO_FIELDNAMES.TIKTOK_HANDLE]: '',
  [PORTFOLIO_FIELDNAMES.INSTAGRAM_HANDLE]: '',
});

export const portfolioInitialValues = getPortfolioInitialValues();
